import {
    lazy,
    createComponent,
    onMount,
} from 'solid-js'

import sessionStore from '../../../stores/session-store.js'
import SessionService from '../../services/session-service.js'
import { usePublicTemplate } from '../../components/templates/public-template/public-template-component-context.js'
import { syncSearchTemplateWithSession } from '../../utils/search-template-session-sync.js'
import { messages } from '../../pages/search-page/search-page-routing.messages.en.js'

const SearchPage = lazy(() => import('../../pages/search-page/search-page.jsx'))

const SearchPageWrapper = (props) => {
    const templateContext = usePublicTemplate()

    const applyTemplateSettings = (authenticated) => {
        if (templateContext.isContextReady && templateContext.isContextReady()) {
            templateContext.setShowSidebar(authenticated)
            templateContext.setShowHeaderMenu(false)
            templateContext.setShowFooter(false)
            templateContext.setShowUserHeader(!authenticated)
        }
    }

    // Initial setup from the persisted token — covers hard refresh, where
    // the session signal hasn't been confirmed yet at mount time.
    onMount(async () => {
        const accessToken = await sessionStore.getAccessToken()
        applyTemplateSettings(Boolean(accessToken))
    })

    // TENANT-5135 A1: the OAuth landing stores its token AFTER this wrapper
    // mounts, so the one-shot mount check used to leave the sidebar hidden
    // until a hard refresh. Re-apply when the session signal confirms.
    // TENANT-5814: a logout that starts outside the page (extension Log Out,
    // another tab, a rejected session) must drop the signed-in chrome too; the
    // helper still ignores the signal's initial `false` so a hard refresh does
    // not flash the logged-in chrome away during boot.
    syncSearchTemplateWithSession({
        applyTemplateSettings,
        isAuthenticated: SessionService.isAuthenticated,
    })

    // TENANT-5929: render the lazy page, never return the `lazy()` factory itself. A returned
    // function is treated as a reactive child: the render effect calls it, tracks the memo it
    // returns, and on solid-js >= 1.9.14 (lazy resets its cache on cleanup) every re-run reloads
    // and re-mounts the page — an endless loop that freezes the tab.
    return createComponent(SearchPage, props)
}

// TODO: Update how we are handling template headers. We should overhaul this system
export default {
    component: SearchPageWrapper,
    descriptionMessage: messages.descriptionMessage,
    // `/search` is a query-driven surface: every combination of dates, guests and coordinates is a
    // separate URL with substantially the same content, and the location landing pages are the
    // indexable entry points into it. `noindex,follow` keeps the crawl budget on those and still
    // lets the crawler follow through to listings. [doc: PRD §9; Phase 5 AC#4]
    noIndex: true,
    path: '/search',
    showFooter: false,
    showFullWidthHeader: true,
    showHeader: true,
    showHeaderMenu: false,
    showSidebar: true,
    // Note: Having to hide user header because it breaks on small screens
    // Note: These are being overridden by the wrapper
    showUserHeader: false,
    titleMessage: messages.titleMessage,
}
